import { InjectProperty } from '@jkhy/frontend-utils/di';
import { ConfigurationService } from '@treasury/core/config';
import { FeatureFlagService } from '@treasury/domain/services/feature-flags';
import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-fdic-digital-sign';
import { css, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

export const tagName = 'login-layout';
@customElement(tagName)
export class LoginLayout extends TmBaseComponent {
    public static styles = [
        css`
            :host {
                overflow: hidden;
                position: relative;
                background: var(--container-primary);
                width: 100%;
                height: 100%;
                --sticky-header-height: 60px;
                padding-top: var(--safe-area-top);
            }

            :host([stickyheader]) {
                position: absolute;
                background: #fff;
            }

            ::slotted([slot='alert']) {
                display: block;
                position: absolute;
                z-index: 10;
                width: 100%;
                padding: 0 0.5rem;
                left: 50%;
                transform: translateX(-50%);
            }

            :host(:not([stickyheader]))::slotted([slot='phase']) {
                display: block;
                height: 100%;
                --tm-footer-position: static;
            }

            @media (min-width: 600px) {
                :host {
                    position: absolute;
                    inset: 0px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    background: var(--container-primary);
                }

                #card {
                    box-shadow: rgb(0 0 0 / 40%) 0px 0px 8px;
                    width: clamp(300px, 95%, var(--login-card-max-width, 600px));
                }

                :host([stickyheader])::slotted([slot='phase']) {
                    --tm-footer-position: static;
                }
            }
        `,
    ];

    @property({ type: String })
    public headerText!: string;

    @property({ reflect: true, type: Boolean })
    public stickyHeader = true;

    @property({ reflect: true, type: Boolean })
    public bottomAffixed = true;

    @property({ type: Boolean })
    public isConsumer = true;

    @InjectProperty({
        async: true,
        token: FeatureFlagService,
    })
    declare private featureFlagService: Promise<FeatureFlagService>;

    @InjectProperty()
    declare private readonly config: ConfigurationService;

    protected async firstUpdated() {
        const phaseSlot = this.querySelector('span[slot="phase"]');
        const ro = new ResizeObserver(() => {
            if (this.stickyHeader && this.bottomAffixed && phaseSlot) {
                this.scrollUp();
            }
        });
        if (phaseSlot) {
            ro.observe(phaseSlot);
        }
    }

    protected render() {
        const contentClasses = {
            'h-full': this.stickyHeader,
            'overflow-scroll': this.stickyHeader,
            'w-full': true,
            'items-center': true,
            'sm:pb-0': this.stickyHeader,
            'pb-[--sticky-header-height]': true,
            'bg-primary': true,
        };
        const cardClasses = {
            'sm:py-0': this.stickyHeader,
            'h-full': this.stickyHeader,
            'sm:h-auto': this.stickyHeader,
            'mx-auto': true,
            'sm:my-[30px]': this.stickyHeader,
            'min-h-[300px]': true,
            'sm:relative': !this.stickyHeader,
            fixed: this.bottomAffixed && !this.stickyHeader,
            relative: !this.bottomAffixed && this.stickyHeader,
            absolute: !this.bottomAffixed && !this.stickyHeader,
            'left-0': true,
            'bottom-0': true,
            'w-full': true,
            'bg-primary': true,
            'sm:rounded-[10px]': true,
            'rounded-none': true,
            'overflow-auto': true,
        };

        return html`
            <!-- ${this.renderDynamicStyles()} -->
            ${this.stickyHeader ? this.renderBrandedStickyHeader() : this.renderBrandedBackground()}
            <slot name="alert"></slot>
            <div id="content-pane" class=${classMap(contentClasses)}>
                <div id="card" class=${classMap(cardClasses)}>${this.renderCardContents()}</div>
            </div>
        `;
    }

    private scrollUp() {
        const cardContainer = this.shadowRoot?.getElementById('card');
        if (!cardContainer) {
            return;
        }
        cardContainer.scrollTop = cardContainer.offsetHeight - cardContainer.scrollHeight;
    }

    private renderDynamicStyles() {
        const url = `/branding/${this.config.institutionId}/background.webp`;
        return html`
            <style>
                #brand-container {
                    background-image: url(${url});
                }
            </style>
        `;
    }

    private renderFdicSign() {
        return html`<tm-fdic-digital-sign
            container="relative z-10 px-4 py-2"
        ></tm-fdic-digital-sign>`;
    }

    private renderBrandedBackground() {
        let img: TemplateResult;
        if (this.isConsumer) {
            const imgSrc = `/branding/${this.config.institutionId}/logo.webp`;
            img = html`<img
                src="${imgSrc}"
                alt="${this.config.institutionId} logo"
                class="bg-white absolute top-[12vh] sm:top-[5vh] right-1/2 translate-x-2/4 max-w-[75%] sm:max-w-80 p-0"
            />`;
        } else {
            img = html`<div
                class="absolute top-[12vh] sm:top-[5vh] right-1/2 translate-x-2/4 max-w-[75%] sm:max-w-80 p-0"
            >
                <slot name="default-logo"></slot>
            </div> `;
        }
        return html`${this.renderFdicSign()}
            <div
                id="brand-container"
                class="bg-primary fixed bg-top bg-no-repeat bg-cover inset-0 h-2/3 sm:h-full place-content-center sm:place-content-start"
            >
                ${img}
            </div>`;
    }

    private renderBrandedStickyHeader() {
        const imgSrc = `/branding/${this.config.institutionId}/logo.webp`;
        return html`
            <div
                id="sticky-header-bar"
                class="sticky flex top-0 left-0 z-20 w-full h-[--sticky-header-height] m-0 p-[--navbar-padding] text-white justify-center bg-[--container-header]"
            >
                <img
                    class="object-contain bg-white"
                    src="${imgSrc}"
                    alt="${this.config.institutionId} logo"
                />
            </div>
        `;
    }

    private renderCardContents() {
        return html`
            <div class="card-header flex items-center justify-between text-[--primary-text-color]">
                <h1 class="text-lg font-medium m-0 pl-4 pt-4">${this.headerText}</h1>
            </div>
            <slot name="phase"></slot>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: LoginLayout;
    }
}
